import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Building, Plus, Loader2, Pencil, Trash2, Search, Phone, Mail, User } from "lucide-react";

export default function ProveedoresPage() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [form, setForm] = useState({ nombre: "", ruc_cedula: "", direccion: "", telefono: "", email: "", contacto: "", notas: "" });
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cuentas-pagar/proveedores${searchTerm ? `?search=${searchTerm}` : ''}`, getAuthHeaders());
      setProveedores(res.data.items || []);
    } catch (err) { toast.error("Error al cargar proveedores"); }
    finally { setLoading(false); }
  }, [getAuthHeaders, searchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.nombre) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editingProveedor) {
        await axios.put(`${API}/cuentas-pagar/proveedores/${editingProveedor.id}`, form, getAuthHeaders());
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/cuentas-pagar/proveedores`, form, getAuthHeaders());
        toast.success("Proveedor creado");
      }
      setShowDialog(false);
      setForm({ nombre: "", ruc_cedula: "", direccion: "", telefono: "", email: "", contacto: "", notas: "" });
      setEditingProveedor(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleEdit = (proveedor) => {
    setEditingProveedor(proveedor);
    setForm({ nombre: proveedor.nombre, ruc_cedula: proveedor.ruc_cedula || "", direccion: proveedor.direccion || "", telefono: proveedor.telefono || "", email: proveedor.email || "", contacto: proveedor.contacto || "", notas: proveedor.notas || "" });
    setShowDialog(true);
  };

  const handleDelete = async (proveedorId) => {
    if (!window.confirm("¿Está seguro de eliminar este proveedor?")) return;
    try {
      await axios.delete(`${API}/cuentas-pagar/proveedores/${proveedorId}`, getAuthHeaders());
      toast.success("Proveedor eliminado");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 overflow-auto">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Building className="w-7 h-7 text-[#0C314C]" />Catálogo de Proveedores
              </h1>
              <p className="text-slate-500 mt-1">Gestiona los proveedores y acreedores</p>
            </div>
            <Button onClick={() => { setEditingProveedor(null); setForm({ nombre: "", ruc_cedula: "", direccion: "", telefono: "", email: "", contacto: "", notas: "" }); setShowDialog(true); }} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
              <Plus className="w-4 h-4 mr-2" />Nuevo Proveedor
            </Button>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar por nombre o RNC/Cédula..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
            </CardContent>
          </Card>

          {/* Providers Grid */}
          {loading ? (
            <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : proveedores.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">No hay proveedores registrados</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {proveedores.map(proveedor => (
                <Card key={proveedor.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0C314C] flex items-center justify-center text-white font-bold">
                          {proveedor.nombre.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{proveedor.nombre}</h3>
                          {proveedor.ruc_cedula && <p className="text-xs text-slate-500">{proveedor.ruc_cedula}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(proveedor)}><Pencil className="w-4 h-4" /></Button>
                        {isAdmin && <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(proveedor.id)}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {proveedor.telefono && <p className="flex items-center gap-2 text-slate-600"><Phone className="w-3 h-3" />{proveedor.telefono}</p>}
                      {proveedor.email && <p className="flex items-center gap-2 text-slate-600"><Mail className="w-3 h-3" />{proveedor.email}</p>}
                      {proveedor.contacto && <p className="flex items-center gap-2 text-slate-600"><User className="w-3 h-3" />{proveedor.contacto}</p>}
                      {proveedor.direccion && <p className="text-slate-500 text-xs">{proveedor.direccion}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle><Building className="w-5 h-5 inline mr-2" />{editingProveedor ? "Editar" : "Nuevo"} Proveedor</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Nombre *</Label><Input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} placeholder="Nombre del proveedor" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>RNC/Cédula</Label><Input value={form.ruc_cedula} onChange={e => setForm({...form, ruc_cedula: e.target.value})} /></div>
                <div><Label>Teléfono</Label><Input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Persona de Contacto</Label><Input value={form.contacto} onChange={e => setForm({...form, contacto: e.target.value})} /></div>
              <div><Label>Dirección</Label><Textarea value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} /></div>
              <div><Label>Notas</Label><Textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
